import { format, parseISO } from 'date-fns'
import { enUS, ru, uk } from 'date-fns/locale'

export type AppLang = 'uk' | 'en' | 'ru'

const DATE_LOCALE = {
  uk,
  en: enUS,
  ru,
} as const

export function localizedName(
  lang: AppLang,
  nameUk: string,
  nameEn?: string | null,
  nameRu?: string | null,
): string {
  if (lang === 'en' && nameEn) return nameEn
  if (lang === 'ru' && nameRu) return nameRu
  return nameUk
}

// Single source of truth for "is this shop a lodging/hotel business" —
// used to fold room-type booking cards into the catalog grid instead of
// running a separate `/lodging` page. Keep in sync if a new lodging shop
// is onboarded with a name that doesn't hit one of these substrings.
export function isLodgingShop(shopName: string | undefined | null): boolean {
  const slug = (shopName ?? '').toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, '')
  return (
    slug.includes('гнездышко') ||
    slug.includes('гнездишко') ||
    slug.includes('gnezdyshko') ||
    slug.includes('gnyozdishko') ||
    slug.includes('отель') ||
    slug.includes('гостиница')
  )
}

// Gnezdyshko prices out of the platform's default UAH — the guesthouse
// is in Crimea, which has used the Russian ruble since 2014, not the
// Ukrainian hryvnia. `base_price_uah`/`total_price_uah` fields keep the
// generic column name from the shared lodging schema; only the display
// currency differs for this shop.
export function formatRoomPrice(amountUah: number): string {
  return `${amountUah.toLocaleString('ru-RU')} руб.`
}

export function displayCurrencyLabel(shopName: string | undefined | null): string {
  return isLodgingShop(shopName) ? 'руб.' : 'UAH'
}

/**
 * Standardized single source of truth for formatting customer prices.
 * Formats numbers with spaces (e.g. 1 250 ₴) consistently across the entire app.
 */
export function formatPrice(
  amountUah: number,
  options?: { shopName?: string | null; currencySymbol?: string }
): string {
  const shopName = options?.shopName
  if (isLodgingShop(shopName)) {
    return formatRoomPrice(amountUah)
  }
  const symbol = options?.currencySymbol ?? '₴'
  const rounded = Math.round(amountUah || 0)
  return `${rounded.toLocaleString('uk-UA')} ${symbol}`
}

export function dayShortLabel(lang: AppLang): string {
  if (lang === 'en') return 'days'
  if (lang === 'ru') return 'дн.'
  return 'дн.'
}

export function stockLabel(lang: AppLang, qty: number): string {
  if (lang === 'en') return `Stock: ${qty}`
  if (lang === 'ru') return `Остаток: ${qty}`
  return `Залишок: ${qty}`
}

export function formatDateTime(iso: string, lang: AppLang, pattern = 'dd.MM.yyyy HH:mm'): string {
  if (!iso) return ''
  try {
    return format(parseISO(iso), pattern, { locale: DATE_LOCALE[lang] })
  } catch {
    return iso
  }
}

export function formatDateTimeRange(start: string, end: string, lang: AppLang): string {
  try {
    const s = parseISO(start)
    const e = parseISO(end)
    return `${format(s, 'dd.MM.yyyy HH:mm', { locale: DATE_LOCALE[lang] })} - ${format(e, 'HH:mm', { locale: DATE_LOCALE[lang] })}`
  } catch {
    return start
  }
}

export function cleanPhoneNumber(phone: string | undefined | null): string {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 10 && digits.startsWith('0')) return `38${digits}`
  if (digits.length === 9 && !digits.startsWith('0')) return `380${digits}`
  return digits
}

export function buildWhatsAppChatUrl(phone: string, text: string): string {
  const clean = cleanPhoneNumber(phone)
  const encoded = encodeURIComponent(text)
  return clean ? `https://wa.me/${clean}?text=${encoded}` : `https://wa.me/?text=${encoded}`
}

export function buildViberChatUrl(phone: string, text: string): string {
  const clean = cleanPhoneNumber(phone)
  const encoded = encodeURIComponent(text)
  return clean ? `viber://chat?number=%2B${clean}&draft=${encoded}` : `viber://forward?text=${encoded}`
}

const CYRILLIC_MAP: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'h',
  ґ: 'g',
  д: 'd',
  е: 'e',
  є: 'ye',
  ж: 'zh',
  з: 'z',
  и: 'y',
  і: 'i',
  ї: 'yi',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ь: '',
  ю: 'yu',
  я: 'ya',
  э: 'e',
  ы: 'y',
  ё: 'yo',
}

export function slugify(text: string): string {
  const lower = text.toLowerCase().trim()
  const translated = Array.from(lower)
    .map((c) => CYRILLIC_MAP[c] ?? c)
    .join('')
  const slug = translated.replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
  return slug || 'item'
}
